import * as THREE from 'three';
import type { TinLod } from '@/core/landxmlLod';
import { displayToInternalRaw, internalToDisplayRaw } from './alignment';
import type { AlignmentDegradation, DrapeSampler, KeyPoint, KeyPointKind, StationEquation } from './alignment';
import { buildTinSampler, combineSamplers } from './tinSampler';
import { LandxmlNonMetreUnitsError, TIN_INDEX_ATTR, type RawTin } from './landxmlTin';

/** Re-exported so callers have one import for "the LandXML readers refused this file". */
export { LandxmlNonMetreUnitsError } from './landxmlTin';

/**
 * Civil categories surfaced to the UI. Used for color-by-category, the
 * properties drawer, and quantity reports.
 */
export type LandXMLCategory =
  | 'SURFACE'
  | 'ALIGNMENT'
  | 'PARCEL'
  | 'BREAKLINE'
  | 'PIPE'
  | 'STRUCTURE';

/**
 * Per-feature metadata. Only the fields relevant to the feature's category
 * are populated — everything else is undefined.
 */
export interface LandXMLFeatureMetadata {
  // Surface
  surfaceArea?: number;       // sum of TIN triangle areas (m²)
  triangleCount?: number;
  vertexCount?: number;
  zMin?: number;
  zMax?: number;
  // Alignment / breakline / pipe centerline
  length?: number;            // 3D polyline length (m)
  alignment?: AlignmentParseResult;
  // Parcel
  parcelArea?: number;        // planar XY area via shoelace (m²)
  // Pipe
  diameter?: number;          // outer diameter (m)
  pipeShape?: PipeShape;
  invertStart?: number;       // invert elevation at start (m)
  invertEnd?: number;         // invert elevation at end (m)
  slope?: number;             // (invertEnd-invertStart)/horizLength
  // Structure
  rimElev?: number;
  sumpElev?: number;
  innerDiameter?: number;
}

export interface LandXMLFeature {
  geometry: THREE.BufferGeometry;
  name: string;
  category: LandXMLCategory;
  metadata: LandXMLFeatureMetadata;
  /**
   * TIN surfaces only: absolute-coordinate vertices kept aside for the drape
   * sampler. `geometry` is translated to layer-local space shortly after
   * parsing, so it cannot be the sampler's source once that has happened —
   * this can, which is what lets `buildSurfaceSampler` defer its grid build.
   */
  drapeSource?: { positions: Float64Array; index: Uint32Array };
  /**
   * TIN surfaces only: a decimated copy drawn in place of `geometry` while the
   * decimation is too fine to see. Built by `buildTinLod` after the origin
   * shift, so it lives in the same layer-local space as `geometry`. Display
   * only — see `core/landxmlLod.ts` for why it must never be measured against.
   */
  lod?: TinLod;
}

export interface PipeNetworkResult {
  /** Pipe + structure meshes (3D solids). */
  meshes: LandXMLFeature[];
  /** Pipe centerlines (lines). */
  lines: LandXMLFeature[];
}

// --- geometry math helpers ----------------------------------------------

function polylineLength(points: ReadonlyArray<THREE.Vector3>): number {
  let len = 0;
  for (let i = 1; i < points.length; i++) len += points[i].distanceTo(points[i - 1]);
  return len;
}

/** Shoelace area of a closed XY polygon (last point may be a duplicate of first). */
function polygonAreaXY(points: ReadonlyArray<THREE.Vector3>): number {
  if (points.length < 3) return 0;
  let s = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) * 0.5;
}

/** Sum of triangle areas in a non-indexed positions buffer (3 verts/tri). */
function trianglesArea(positions: number[]): number {
  let total = 0;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  for (let i = 0; i + 8 < positions.length; i += 9) {
    a.set(positions[i], positions[i + 1], positions[i + 2]);
    b.set(positions[i + 3], positions[i + 4], positions[i + 5]);
    c.set(positions[i + 6], positions[i + 7], positions[i + 8]);
    ab.subVectors(b, a);
    ac.subVectors(c, a);
    total += ab.cross(ac).length() * 0.5;
  }
  return total;
}

// Parse a space-separated coordinate string "northing easting elevation" into [easting, northing, elevation] (Z-up)
const parsePoint = (text: string): [number, number, number] => {
  const parts = text.trim().split(/\s+/).map(Number);
  return [parts[1], parts[0], parts[2] ?? 0];
};

/**
 * Raw declared `linearUnit` string from `<Units>`, or null if none is
 * declared. Scans ALL of `<Units>`'s children for the first with a
 * `linearUnit` attribute — not just child[0], which may be a leading
 * `<Feature/>`. No validation, no table lookup: just "what did the file
 * say". The one place in this module that reads the attribute; every guard
 * below goes through `assertMetreUnits`.
 */
function declaredLinearUnit(doc: Document): string | null {
  const unitsEl = doc.querySelector('Units');
  if (!unitsEl) return null;
  for (const child of Array.from(unitsEl.children)) {
    const candidate = child.getAttribute('linearUnit');
    if (candidate !== null) return candidate;
  }
  return null;
}

/** The only `linearUnit` spellings these readers render (lower-cased, trimmed). */
const METRE_UNITS = new Set(['meter', 'metre']);

/**
 * DOM counterpart to `assertLandxmlMetres` in `landxmlTin.ts` — same rule,
 * same error, for documents already parsed into a `Document` rather than read
 * from raw text: metres pass, an absent `<Units>` (or no `linearUnit` on any
 * of its children) is assumed to be metres with a console.warn, and every
 * other declared unit throws `LandxmlNonMetreUnitsError`.
 *
 * Nothing here scales. Surfaces once did (the TIN reader's table, mirrored
 * from `scripts/surface_io.py`), but alignments, breaklines, parcels and pipe
 * networks never could: their curve radius/length attributes, station
 * equations, profile data, parcel areas and pipe diameters are all plain
 * numbers in the file's own unit. A half-converted file — points scaled,
 * radii not — looks plausible and is worse than a refused one, and the
 * server-side reader refuses non-metre files outright, so the client agreeing
 * is the whole point (corridor-trust T5).
 */
function assertMetreUnits(doc: Document): void {
  const unit = declaredNonMetreUnit(doc);
  if (unit !== null) throw new LandxmlNonMetreUnitsError(unit);
}

/**
 * The declared unit when it is one this reader refuses, else null (metres, or
 * an absent `<Units>` — warned about once, here). Separate from
 * `assertMetreUnits` so the parsers below can resolve (and warn) once at the
 * top while still throwing only where they would actually have rendered
 * something: a file whose `<Parcels>`/`<Breaklines>` container is empty has
 * nothing to refuse.
 */
function declaredNonMetreUnit(doc: Document): string | null {
  const linearUnit = declaredLinearUnit(doc);
  if (linearUnit === null) {
    console.warn('LandXML: no <Units>/linearUnit found; assuming metres.');
    return null;
  }
  return METRE_UNITS.has(linearUnit.trim().toLowerCase()) ? null : linearUnit;
}

export interface AlignmentParseResult {
  points: Float64Array;
  staStart: number;
  equations: StationEquation[];
  keyPoints: KeyPoint[];
  declaredLength?: number;
  zSource: 'COORDGEOM' | 'PROFILE' | 'DRAPED' | 'NONE';
  /** Elements drawn as straight chords (see `AlignmentDegradation`), station order. */
  degradations: AlignmentDegradation[];
}

type P3 = [number, number, number];

/** Direct children with a given local name (namespace-agnostic — works in browsers and jsdom). */
const childrenNamed = (el: Element, name: string): Element[] =>
  Array.from(el.children).filter((c) => c.localName === name);
const firstChild = (el: Element, name: string): Element | null => childrenNamed(el, name)[0] ?? null;
const textOf = (el: Element | null): string | null => (el?.textContent ? el.textContent : null);
const numAttr = (el: Element, name: string): number | null => {
  const v = el.getAttribute(name);
  if (v === null) return null;
  if (/^inf$/i.test(v.trim())) return Infinity;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const MAX_CHORD_DEVIATION = 0.02; // m
const MAX_SEGMENT_ANGLE = (2 * Math.PI) / 180; // 2°
const MAX_ARC_SEGMENTS = 512;

/** Radius (average of the two declared-endpoint distances) + normalised sweep angle for an arc about `center`. */
function arcRadiusAndSweep(start: P3, end: P3, center: [number, number], ccw: boolean): { r: number; sweep: number } {
  const r = (Math.hypot(start[0] - center[0], start[1] - center[1]) + Math.hypot(end[0] - center[0], end[1] - center[1])) / 2;
  const a0 = Math.atan2(start[1] - center[1], start[0] - center[0]);
  const a1 = Math.atan2(end[1] - center[1], end[0] - center[0]);
  let sweep = ccw ? a1 - a0 : a0 - a1;
  while (sweep <= 1e-12) sweep += 2 * Math.PI;
  while (sweep > 2 * Math.PI) sweep -= 2 * Math.PI;
  return { r, sweep };
}

/**
 * True (analytic) arc length — used for stationing so key-point stations
 * reflect the design curve, not the chord-length sum of its tessellation.
 */
function arcAnalyticLength(start: P3, end: P3, center: [number, number], ccw: boolean): number {
  const { r, sweep } = arcRadiusAndSweep(start, end, center, ccw);
  return r * sweep;
}

/** Points along a circular arc from `start` to `end` about `center` (excluding `start`). */
function tessellateArc(start: P3, end: P3, center: [number, number], ccw: boolean): P3[] {
  const { r, sweep } = arcRadiusAndSweep(start, end, center, ccw);
  if (r < 1e-6) return [end];
  const a0 = Math.atan2(start[1] - center[1], start[0] - center[0]);
  const maxAngle = Math.min(MAX_SEGMENT_ANGLE, 2 * Math.acos(Math.max(-1, 1 - MAX_CHORD_DEVIATION / r)));
  let n = Math.min(MAX_ARC_SEGMENTS, Math.max(1, Math.ceil(sweep / maxAngle)));
  // Keep the segment count even so a sample always lands exactly on the arc's
  // angular midpoint (t=0.5) — needed for consumers that snap to it (e.g. a
  // picked point exactly at the bisector), and harmless everywhere else.
  if (n % 2 !== 0) n = Math.min(MAX_ARC_SEGMENTS, n + 1);
  const out: P3[] = [];
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const a = a0 + (ccw ? 1 : -1) * sweep * t;
    out.push([center[0] + r * Math.cos(a), center[1] + r * Math.sin(a), start[2] + (end[2] - start[2]) * t]);
  }
  out[out.length - 1] = end; // snap exactly onto the declared end
  return out;
}

/** Centre from radius + chord + rotation (+ optional declared length to pick the long arc). */
function arcCenterFromRadius(start: P3, end: P3, radius: number, ccw: boolean, length: number | null): [number, number] | null {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const c = Math.hypot(dx, dy);
  if (c < 1e-9 || radius < c / 2 - 1e-6) return null;
  const h = Math.sqrt(Math.max(0, radius * radius - (c * c) / 4));
  const mx = start[0] + dx / 2;
  const my = start[1] + dy / 2;
  const lx = -dy / c; // left normal
  const ly = dx / c;
  const longArc = length !== null && length > Math.PI * radius;
  const side = (ccw ? 1 : -1) * (longArc ? -1 : 1);
  return [mx + side * h * lx, my + side * h * ly];
}

/**
 * Clothoid from `start` to `end`. Integrates heading θ(s) = k0·s + (k1−k0)·s²/(2L)
 * in a local frame, then rotates so the local chord matches the world chord —
 * exact regardless of <PI>. Falls back to a straight chord on mismatch.
 */
function tessellateSpiral(start: P3, end: P3, length: number, rStart: number, rEnd: number, ccw: boolean): P3[] | null {
  if (!(length > 0)) return null;
  const k0 = Number.isFinite(rStart) && rStart > 0 ? 1 / rStart : 0;
  const k1 = Number.isFinite(rEnd) && rEnd > 0 ? 1 / rEnd : 0;
  const sgn = ccw ? 1 : -1;
  const samples = Math.max(8, Math.ceil(length / 2));
  const sub = 8; // integration sub-steps per sample
  const ds = length / (samples * sub);
  const xs = new Float64Array(samples + 1);
  const ys = new Float64Array(samples + 1);
  let x = 0, y = 0, s = 0;
  let prevC = 1, prevS = 0;
  for (let i = 1; i <= samples * sub; i++) {
    s += ds;
    const th = sgn * (k0 * s + ((k1 - k0) * s * s) / (2 * length));
    const c = Math.cos(th), sn = Math.sin(th);
    x += ((c + prevC) / 2) * ds;
    y += ((sn + prevS) / 2) * ds;
    prevC = c;
    prevS = sn;
    if (i % sub === 0) {
      xs[i / sub] = x;
      ys[i / sub] = y;
    }
  }
  const localChord = Math.atan2(ys[samples], xs[samples]);
  const worldChord = Math.atan2(end[1] - start[1], end[0] - start[0]);
  const rot = worldChord - localChord;
  const cr = Math.cos(rot), sr = Math.sin(rot);
  const out: P3[] = [];
  for (let i = 1; i <= samples; i++) {
    const t = i / samples;
    out.push([start[0] + xs[i] * cr - ys[i] * sr, start[1] + xs[i] * sr + ys[i] * cr, start[2] + (end[2] - start[2]) * t]);
  }
  const last = out[out.length - 1];
  if (Math.hypot(last[0] - end[0], last[1] - end[1]) > 0.5) return null;
  out[out.length - 1] = end;
  return out;
}

type GeomElementKind = 'Line' | 'Curve' | 'Spiral';

/** Parse <CoordGeom> into a polyline plus key points (internal stations). */
function parseCoordGeomDetailed(
  coordGeom: Element,
): { points: P3[]; keyPoints: KeyPoint[]; warnings: string[]; degradations: AlignmentDegradation[]; length: number } {
  const points: P3[] = [];
  const keyPoints: KeyPoint[] = [];
  const warnings: string[] = [];
  // One entry per chord fallback below. Same events as `warnings`, but
  // structured and carried on the parse result — the console line alone left
  // the user with a corner-cutting alignment and no way to know.
  const degradations: AlignmentDegradation[] = [];
  let station = 0;
  let prevKind: GeomElementKind | null = null;

  // Push tessellated points onto the polyline WITHOUT advancing `station` —
  // station is tracked separately below using each element's true (analytic)
  // length, not the chord-length sum of its tessellation. Key-point stations
  // must reflect the design curve regardless of tessellation density.
  const push = (pts: P3[]) => {
    for (const p of pts) points.push(p);
  };
  const transition = (from: GeomElementKind | null, to: GeomElementKind): KeyPointKind | null => {
    if (from === null) return null;
    if (from === 'Line' && to === 'Curve') return 'PC';
    if (from === 'Curve' && to === 'Line') return 'PT';
    if (from === 'Line' && to === 'Spiral') return 'TS';
    if (from === 'Spiral' && to === 'Curve') return 'SC';
    if (from === 'Curve' && to === 'Spiral') return 'CS';
    if (from === 'Spiral' && to === 'Line') return 'ST';
    return null;
  };

  for (const el of Array.from(coordGeom.children)) {
    const kind = el.localName as GeomElementKind;
    if (kind !== 'Line' && kind !== 'Curve' && kind !== 'Spiral') continue;
    const startTxt = textOf(firstChild(el, 'Start'));
    const endTxt = textOf(firstChild(el, 'End'));
    if (!startTxt || !endTxt) continue;
    const start = parsePoint(startTxt);
    const end = parsePoint(endTxt);
    if (points.length === 0) points.push(start);
    const kp = transition(prevKind, kind);
    if (kp) keyPoints.push({ station, kind: kp });
    const piTxt = textOf(firstChild(el, 'PI'));
    if (piTxt && kind !== 'Line') keyPoints.push({ station, kind: 'PI' });

    const chordLen = Math.hypot(end[0] - start[0], end[1] - start[1]);
    if (kind === 'Line') {
      push([end]);
      station += chordLen;
    } else if (kind === 'Curve') {
      const ccw = (el.getAttribute('rot') || 'cw').toLowerCase() === 'ccw';
      const centerTxt = textOf(firstChild(el, 'Center'));
      let center: [number, number] | null = null;
      if (centerTxt) {
        const c = parsePoint(centerTxt);
        center = [c[0], c[1]];
      } else {
        const radius = numAttr(el, 'radius');
        center = radius ? arcCenterFromRadius(start, end, radius, ccw, numAttr(el, 'length')) : null;
      }
      if (center) {
        push(tessellateArc(start, end, center, ccw));
        station += arcAnalyticLength(start, end, center, ccw);
      } else {
        warnings.push('Curve without usable centre/radius — drawn as chord');
        degradations.push({ kind: 'CURVE_CHORD', element: 'Curve', staInternalM: station, lengthM: chordLen });
        push([end]);
        station += chordLen;
      }
    } else {
      const ccw = (el.getAttribute('rot') || 'cw').toLowerCase() === 'ccw';
      const spiType = (el.getAttribute('spiType') || 'clothoid').toLowerCase();
      const length = numAttr(el, 'length');
      const rStart = numAttr(el, 'radiusStart') ?? Infinity;
      const rEnd = numAttr(el, 'radiusEnd') ?? Infinity;
      const pts = spiType === 'clothoid' && length ? tessellateSpiral(start, end, length, rStart, rEnd, ccw) : null;
      if (pts) {
        push(pts);
        station += length!; // a clothoid's declared length IS its analytic arc length
      } else {
        warnings.push(`Spiral (${spiType}) not supported — drawn as chord`);
        degradations.push({
          kind: 'SPIRAL_CHORD',
          element: `Spiral (${spiType})`,
          staInternalM: station,
          lengthM: chordLen,
        });
        push([end]);
        station += chordLen;
      }
    }
    prevKind = kind;
  }
  return { points, keyPoints, warnings, degradations, length: station };
}

/**
 * Parse <StaEquation> children into our 0-based internal stations.
 *
 * LandXML 1.2 defines `staInternal` as "the station value with NO equations
 * applied", i.e. `staStart + distance-along-the-alignment`
 * (landxml.org, LandXML-1.2Doc_StaEquation). Our `StationEquation.staInternal`
 * is a plain 0-based distance from the first vertex, so we subtract `staStart`.
 *
 * `staBack` is only a fallback: it is the *display* station immediately BEHIND
 * the equation, expressed in the stationing that is in force there. For the
 * first equation that stationing is `staStart`-based, so `staBack - staStart`
 * is exact; for the 2nd and later equations it is the PREVIOUS equation's
 * ahead stationing, which we cannot undo without knowing the whole chain — so
 * only the first equation's fallback is reliable. Prefer `staInternal`.
 */
function parseStationEquations(alignment: Element, staStart: number): StationEquation[] {
  const out: StationEquation[] = [];
  for (const eq of childrenNamed(alignment, 'StaEquation')) {
    const ahead = numAttr(eq, 'staAhead');
    if (ahead === null) continue;
    const declared = numAttr(eq, 'staInternal');
    let internal: number;
    if (declared !== null) {
      internal = declared - staStart;
    } else {
      const back = numAttr(eq, 'staBack');
      if (back === null) continue;
      internal = back - staStart;
    }
    out.push({ staInternal: internal, staAhead: ahead });
  }
  return out.sort((a, b) => a.staInternal - b.staInternal);
}

export const parseBreaklines = (doc: Document): LandXMLFeature[] => {
  const results: LandXMLFeature[] = [];
  const surfaces = doc.querySelectorAll('Surface');
  // Only resolve/warn when there is actually a <Breakline> somewhere —
  // otherwise every breakline-less file would pay a redundant "no <Units>"
  // console.warn on top of parseSurfaces's own (mirrors parseAlignments).
  const hasBreaklines = doc.querySelectorAll('SourceData > Breaklines > Breakline').length > 0;
  const nonMetreUnit = hasBreaklines ? declaredNonMetreUnit(doc) : null;

  for (const surface of Array.from(surfaces)) {
    const surfaceName = surface.getAttribute('name') || 'Surface';
    const sourceData = surface.querySelector('SourceData');
    if (!sourceData) continue;

    const breaklinesContainer = sourceData.querySelector('Breaklines');
    if (!breaklinesContainer) continue;

    const breaklineElements = breaklinesContainer.querySelectorAll('Breakline');
    for (const breakline of Array.from(breaklineElements)) {
      const breaklineName = breakline.getAttribute('name') || 'Breakline';
      const pntList3D = breakline.querySelector('PntList3D');

      if (pntList3D?.textContent) {
        // Non-metre coordinates are refused, never scaled (see
        // `declaredNonMetreUnit`). Fires only when there is a real breakline
        // (PntList3D content) to refuse.
        if (nonMetreUnit !== null) throw new LandxmlNonMetreUnitsError(nonMetreUnit);
        const coords = pntList3D.textContent.trim().split(/\s+/).map(Number);
        const points: THREE.Vector3[] = [];

        for (let i = 0; i < coords.length; i += 3) {
          const northing = coords[i];
          const easting = coords[i + 1];
          const elevation = coords[i + 2] ?? 0;
          points.push(new THREE.Vector3(easting, northing, elevation));
        }

        if (points.length >= 2) {
          const geometry = new THREE.BufferGeometry().setFromPoints(points);
          results.push({
            geometry,
            name: `${surfaceName} - ${breaklineName}`,
            category: 'BREAKLINE',
            metadata: { length: polylineLength(points) },
          });
        }
      }
    }
  }

  return results;
};

/**
 * Turn an already-extracted TIN into the same `LandXMLFeature` the DOM path
 * below produces. The geometry is INDEXED, so `computeVertexNormals` averages
 * across shared faces (smooth shading) instead of emitting one normal per
 * triangle corner.
 */
const featureFromTin = (name: string, tin: RawTin): LandXMLFeature | null => {
  if (tin.triangleCount === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(tin.positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(tin.index, 1));
  geometry.computeVertexNormals();
  flipNormalsUp(geometry);
  return {
    geometry,
    name,
    category: 'SURFACE',
    // Absolute-coordinate snapshot for draping. LandXML.tsx translates the
    // geometry by -origin immediately after parsing; this survives that.
    drapeSource: { positions: tin.positions64, index: tin.index },
    metadata: {
      surfaceArea: tin.surfaceArea,
      triangleCount: tin.triangleCount,
      vertexCount: tin.vertexCount,
      zMin: tin.zMin,
      zMax: tin.zMax,
    },
  };
};

/**
 * Flip any normal pointing below the horizon so a TIN is always lit from
 * above, whichever way its faces happen to be wound.
 */
const flipNormalsUp = (geometry: THREE.BufferGeometry) => {
  const normals = geometry.getAttribute('normal');
  if (!normals) return;
  for (let i = 0; i < normals.count; i++) {
    if (normals.getZ(i) < 0) {
      normals.setX(i, -normals.getX(i));
      normals.setY(i, -normals.getY(i));
      normals.setZ(i, -normals.getZ(i));
    }
  }
  normals.needsUpdate = true;
};

/**
 * `tins` are the meshes `extractTins` already pulled out of the raw text; a
 * `<Pnts>` carrying `TIN_INDEX_ATTR` is served from there instead of being
 * re-read from the DOM. Called without them (tests, small files, any document
 * `extractTins` declined) this behaves exactly as it always has.
 */
export const parseSurfaces = (doc: Document, tins: RawTin[] = []): LandXMLFeature[] => {
  const results: LandXMLFeature[] = [];
  const surfaces = doc.querySelectorAll('Surface');
  // Metres or nothing, for both paths: `extractTins` has already refused a
  // non-metre file before it could reach the fast path, and this is the same
  // refusal for the per-point DOM fallback below (a surface `extractTins`
  // declined, or none was supplied). Eager, unlike the guards in the other
  // parsers: a non-metre file is refused whole, and this is the first reader
  // `LandXML.tsx` calls, so it is where the one toast comes from.
  assertMetreUnits(doc);

  for (const surface of Array.from(surfaces)) {
    const name = surface.getAttribute('name') || 'Surface';
    const definition = surface.querySelector('Definition');
    if (!definition) continue;

    const pntsEl = definition.querySelector('Pnts');
    const facesEl = definition.querySelector('Faces');
    if (!pntsEl || !facesEl) continue;

    const tinIndex = Number(pntsEl.getAttribute(TIN_INDEX_ATTR));
    if (pntsEl.hasAttribute(TIN_INDEX_ATTR) && tins[tinIndex]) {
      const feature = featureFromTin(name, tins[tinIndex]);
      if (feature) results.push(feature);
      continue;
    }

    const pointMap = new Map<number, [number, number, number]>();
    const pElements = pntsEl.querySelectorAll('P');
    for (const p of Array.from(pElements)) {
      const id = parseInt(p.getAttribute('id') || '0');
      if (p.textContent) {
        const [x, y, z] = parsePoint(p.textContent);
        pointMap.set(id, [x, y, z]);
      }
    }

    const fElements = facesEl.querySelectorAll('F');
    const positions: number[] = [];
    let zMin = Infinity;
    let zMax = -Infinity;
    let triangleCount = 0;
    for (const f of Array.from(fElements)) {
      if (!f.textContent) continue;
      const ids = f.textContent.trim().split(/\s+/).map(Number);
      if (ids.length < 3) continue;

      const p0 = pointMap.get(ids[0]);
      const p1 = pointMap.get(ids[1]);
      const p2 = pointMap.get(ids[2]);
      if (!p0 || !p1 || !p2) continue;

      positions.push(p0[0], p0[1], p0[2]);
      positions.push(p1[0], p1[1], p1[2]);
      positions.push(p2[0], p2[1], p2[2]);
      for (const z of [p0[2], p1[2], p2[2]]) {
        if (z < zMin) zMin = z;
        if (z > zMax) zMax = z;
      }
      triangleCount++;
    }

    if (positions.length === 0) continue;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    flipNormalsUp(geometry);

    results.push({
      geometry,
      name,
      category: 'SURFACE',
      metadata: {
        surfaceArea: trianglesArea(positions),
        triangleCount,
        vertexCount: pointMap.size,
        zMin: Number.isFinite(zMin) ? zMin : undefined,
        zMax: Number.isFinite(zMax) ? zMax : undefined,
      },
    });
  }

  return results;
};

export const parseAlignments = (doc: Document, opts: { drape?: DrapeSampler | null } = {}): LandXMLFeature[] => {
  const results: LandXMLFeature[] = [];
  const alignmentsEl = Array.from(doc.getElementsByTagNameNS('*', 'Alignments'));
  // Only resolve/warn when there is actually an <Alignments> container —
  // otherwise every surfaces-only (or alignment-less) file would pay a
  // redundant "no <Units>" console.warn on top of parseSurfaces's own.
  const nonMetreUnit = alignmentsEl.length > 0 ? declaredNonMetreUnit(doc) : null;
  for (const container of alignmentsEl) {
    for (const alignment of childrenNamed(container, 'Alignment')) {
      const name = alignment.getAttribute('name') || 'Alignment';
      const coordGeom = firstChild(alignment, 'CoordGeom');
      if (!coordGeom) continue;
      // Refused, never scaled: an alignment's CoordGeom radius/length/
      // radiusStart/radiusEnd attributes, staStart/StaEquation stations and
      // Profile PVI stations/elevations are all plain numbers in the file's
      // own unit, so a points-only conversion would look plausible and be
      // wrong everywhere it matters. The server-side reader refuses non-metre
      // files too (corridor-trust T5).
      if (nonMetreUnit !== null) throw new LandxmlNonMetreUnitsError(nonMetreUnit);
      const { points: tuples, keyPoints, warnings, degradations, length: analyticLength2D } = parseCoordGeomDetailed(coordGeom);
      for (const w of warnings) console.warn(`LandXML alignment "${name}": ${w}`);
      if (tuples.length < 2) continue;

      const staStart = numAttr(alignment, 'staStart') ?? 0;
      const equations = parseStationEquations(alignment, staStart);
      const declaredLength = numAttr(alignment, 'length') ?? undefined;

      let points = new Float64Array(tuples.length * 3);
      tuples.forEach(([x, y, z], i) => {
        points[i * 3] = x;
        points[i * 3 + 1] = y;
        points[i * 3 + 2] = z;
      });
      let zSource: AlignmentParseResult['zSource'] = isFlatArray(points) ? 'NONE' : 'COORDGEOM';
      // Profile / drape: filled in by applyElevation (Task 6).
      ({ points, zSource } = applyElevation(alignment, points, zSource, staStart, equations, opts.drape ?? null));

      const polyline: THREE.Vector3[] = [];
      for (let i = 0; i < points.length; i += 3) polyline.push(new THREE.Vector3(points[i], points[i + 1], points[i + 2]));
      const geometry = new THREE.BufferGeometry().setFromPoints(polyline);
      // Analytic (design) 2D length — sum of each element's true length, not
      // the chord-sum of the rendered tessellation, so it matches <Alignment
      // length="..."> to floating-point precision regardless of tessellation
      // density (see parseCoordGeomDetailed).
      const length2D = analyticLength2D;

      results.push({
        geometry,
        name,
        category: 'ALIGNMENT',
        metadata: {
          length: length2D,
          alignment: { points, staStart, equations, keyPoints, declaredLength, zSource, degradations },
        },
      });
    }
  }
  return results;
};

function isFlatArray(points: Float64Array): boolean {
  const z0 = points[2];
  for (let i = 5; i < points.length; i += 3) if (Math.abs(points[i] - z0) > 1e-6) return false;
  return true;
}

interface ProfilePvi {
  station: number; // display station as written in the file
  elev: number;
  curveLength: number; // 0 for a plain PVI
}

/** Parse the first <Profile><ProfAlign> of an alignment into PVIs. */
function parseProfile(alignment: Element): ProfilePvi[] | null {
  const profile = firstChild(alignment, 'Profile');
  const profAlign = profile ? firstChild(profile, 'ProfAlign') : null;
  if (!profAlign) return null;
  const out: ProfilePvi[] = [];
  for (const el of Array.from(profAlign.children)) {
    const txt = el.textContent?.trim();
    if (!txt) continue;
    const [sta, elev] = txt.split(/\s+/).map(Number);
    if (!Number.isFinite(sta) || !Number.isFinite(elev)) continue;
    let curveLength = 0;
    switch (el.localName) {
      case 'PVI':
        break;
      case 'ParaCurve':
      case 'CircCurve':
        curveLength = numAttr(el, 'length') ?? 0;
        break;
      case 'UnsymParaCurve':
        curveLength = (numAttr(el, 'lengthIn') ?? 0) + (numAttr(el, 'lengthOut') ?? 0);
        break;
      default:
        continue;
    }
    out.push({ station: sta, elev, curveLength });
  }
  return out.length >= 2 ? out.sort((a, b) => a.station - b.station) : null;
}

/** Elevation of a PVI profile at a display station (symmetric parabolic vertical curves). */
function profileElevation(pvis: ProfilePvi[], station: number): number {
  if (station <= pvis[0].station) return pvis[0].elev;
  const last = pvis[pvis.length - 1];
  if (station >= last.station) return last.elev;
  // find the tangent [i, i+1] containing station
  let i = 0;
  while (i < pvis.length - 2 && pvis[i + 1].station <= station) i++;
  const a = pvis[i];
  const b = pvis[i + 1];
  // Two PVIs at the same station would make every grade below Infinity/NaN and
  // poison the vertex Z. Treat the pair as a step and hold `a`'s elevation.
  const run = b.station - a.station;
  if (!(run > 1e-9)) return a.elev;
  const grade = (b.elev - a.elev) / run;
  // vertical curve at PVI b?
  const half = b.curveLength / 2;
  if (half > 0 && station >= b.station - half && i + 2 < pvis.length) {
    const c = pvis[i + 2];
    const g2 = (c.elev - b.elev) / (c.station - b.station);
    const x = station - (b.station - half);
    const bvc = b.elev - grade * half;
    return bvc + grade * x + ((g2 - grade) * x * x) / (2 * b.curveLength);
  }
  // vertical curve at PVI a?
  const halfA = a.curveLength / 2;
  if (halfA > 0 && station <= a.station + halfA && i > 0) {
    const p = pvis[i - 1];
    const g1 = (a.elev - p.elev) / (a.station - p.station);
    const x = station - (a.station - halfA);
    const bvc = a.elev - g1 * halfA;
    return bvc + g1 * x + ((grade - g1) * x * x) / (2 * a.curveLength);
  }
  return a.elev + grade * (station - a.station);
}

/** Insert extra vertices at the given internal stations so profile break points are honoured. */
function insertVerticesAt(points: Float64Array, stations: number[]): Float64Array {
  const n = points.length / 3;
  const cum: number[] = [0];
  for (let i = 1; i < n; i++) cum.push(cum[i - 1] + Math.hypot(points[i * 3] - points[i * 3 - 3], points[i * 3 + 1] - points[i * 3 - 2]));
  const wanted = [...new Set(stations.filter((s) => s > 1e-6 && s < cum[n - 1] - 1e-6))].sort((a, b) => a - b);
  const out: number[] = [];
  let w = 0;
  for (let i = 0; i < n; i++) {
    out.push(points[i * 3], points[i * 3 + 1], points[i * 3 + 2]);
    if (i === n - 1) break;
    const segLen = cum[i + 1] - cum[i];
    while (w < wanted.length && wanted[w] < cum[i + 1] - 1e-6) {
      if (wanted[w] > cum[i] + 1e-6 && segLen > 1e-9) {
        const t = (wanted[w] - cum[i]) / segLen;
        out.push(
          points[i * 3] + (points[i * 3 + 3] - points[i * 3]) * t,
          points[i * 3 + 1] + (points[i * 3 + 4] - points[i * 3 + 1]) * t,
          points[i * 3 + 2] + (points[i * 3 + 5] - points[i * 3 + 2]) * t,
        );
      }
      w++;
    }
  }
  return new Float64Array(out);
}

function applyElevation(
  alignment: Element,
  points: Float64Array,
  zSource: AlignmentParseResult['zSource'],
  staStart: number,
  equations: StationEquation[],
  drape: DrapeSampler | null,
): { points: Float64Array; zSource: AlignmentParseResult['zSource'] } {
  const pvis = parseProfile(alignment);
  let length = 0;
  for (let i = 3; i < points.length; i += 3) length += Math.hypot(points[i] - points[i - 3], points[i + 1] - points[i - 2]);

  if (pvis) {
    // Add vertices at PVIs, BVCs and EVCs so the vertical curve shape survives.
    const breakDisplay: number[] = [];
    for (const p of pvis) {
      breakDisplay.push(p.station);
      if (p.curveLength > 0) breakDisplay.push(p.station - p.curveLength / 2, p.station + p.curveLength / 2);
    }
    const breakInternal = breakDisplay
      .map((d) => displayToInternalRaw(d, staStart, equations, length))
      .filter((s): s is number => s !== null);
    // Also densify long straight segments so parabolas aren't chords (every 10 m inside curves).
    for (const p of pvis) {
      if (p.curveLength <= 0) continue;
      for (let d = p.station - p.curveLength / 2; d <= p.station + p.curveLength / 2; d += 10) {
        const s = displayToInternalRaw(d, staStart, equations, length);
        if (s !== null) breakInternal.push(s);
      }
    }
    const dense = insertVerticesAt(points, breakInternal);
    // Evaluate Z at each vertex's display station.
    const out = new Float64Array(dense);
    let cum = 0;
    for (let i = 0; i < out.length; i += 3) {
      if (i > 0) cum += Math.hypot(out[i] - out[i - 3], out[i + 1] - out[i - 2]);
      out[i + 2] = profileElevation(pvis, internalToDisplayRaw(cum, staStart, equations));
    }
    return { points: out, zSource: 'PROFILE' };
  }

  if (zSource === 'COORDGEOM') return { points, zSource };

  if (drape) {
    const out = new Float64Array(points);
    let hit = 0;
    for (let i = 0; i < out.length; i += 3) {
      const z = drape(out[i], out[i + 1]);
      if (z !== null) {
        out[i + 2] = z;
        hit++;
      }
    }
    if (hit > 0) return { points: out, zSource: 'DRAPED' };
  }
  return { points, zSource: 'NONE' };
}

/**
 * Sampler over every SURFACE feature returned by parseSurfaces(), answering
 * in whatever frame the geometries are in when it is built. Call it on the
 * ABSOLUTE (pre-shift) positions if you want world-coordinate answers —
 * LandXML.tsx translates surfaces by `-origin` for float32 precision, so it
 * builds the sampler first. The returned sampler owns a snapshot of the
 * positions (see buildTinSampler), so translating the geometries afterwards
 * does not disturb it.
 */
export function buildSurfaceSampler(surfaces: LandXMLFeature[]): DrapeSampler {
  // Snapshot the inputs NOW — the caller translates every geometry by -origin
  // moments later, and a sampler must keep answering in absolute coordinates.
  // Surfaces from the fast path already carry an absolute `drapeSource`, so
  // they cost nothing here; only DOM-parsed ones need copying.
  const sources = surfaces
    .filter((s) => s.category === 'SURFACE')
    .map((s) => {
      if (s.drapeSource) return s.drapeSource;
      const p = s.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
      return p ? { positions: Float64Array.from(p.array as ArrayLike<number>), index: undefined } : null;
    })
    .filter((s): s is { positions: Float64Array; index?: Uint32Array } => !!s);

  // Building the uniform grid is the expensive half — for a 2.4 M-triangle TIN
  // it allocates ~700 k JS arrays. Most LandXML layers are never draped onto
  // (a pure DTM export has no alignments at all), so defer it until the first
  // query actually arrives.
  let grid: DrapeSampler | null = null;
  return (x, y) => {
    if (!grid) grid = combineSamplers(sources.map((s) => buildTinSampler(s.positions, s.index)));
    return grid(x, y);
  };
}

export const parseParcels = (doc: Document): LandXMLFeature[] => {
  const results: LandXMLFeature[] = [];
  const parcels = doc.querySelectorAll('Parcels > Parcel');
  // Only resolve/warn when there is actually a <Parcels> container —
  // otherwise every parcel-less file would pay a redundant "no <Units>"
  // console.warn on top of parseSurfaces's own (mirrors parseAlignments).
  const nonMetreUnit = parcels.length > 0 ? declaredNonMetreUnit(doc) : null;

  for (const parcel of Array.from(parcels)) {
    const name = parcel.getAttribute('name') || 'Parcel';
    const coordGeom = parcel.querySelector('CoordGeom');
    if (!coordGeom) continue;
    // Parcel coordinates/areas are refused, never scaled (see
    // `declaredNonMetreUnit`). Fires only when there is a real parcel (a
    // CoordGeom boundary) to refuse.
    if (nonMetreUnit !== null) throw new LandxmlNonMetreUnitsError(nonMetreUnit);

    const tuples = parseCoordGeomDetailed(coordGeom).points;
    if (tuples.length < 2) continue;

    // Close the boundary
    if (
      tuples.length > 2 &&
      (tuples[0][0] !== tuples[tuples.length - 1][0] ||
        tuples[0][1] !== tuples[tuples.length - 1][1] ||
        tuples[0][2] !== tuples[tuples.length - 1][2])
    ) {
      tuples.push(tuples[0]);
    }

    const polyline = tuples.map(([x, y, z]) => new THREE.Vector3(x, y, z));
    const geometry = new THREE.BufferGeometry().setFromPoints(polyline);
    results.push({
      geometry,
      name,
      category: 'PARCEL',
      metadata: {
        parcelArea: polygonAreaXY(polyline),
        length: polylineLength(polyline),
      },
    });
  }

  return results;
};

// --- Pipe Network types and helpers ---

interface ParsedStruct {
  name: string;
  center: [number, number, number];
  innerDiameter: number;
  outerDiameter: number;
  height: number;
  invertElevs: Map<string, number>;
}

export type PipeShape = 'circular' | 'egg' | 'elliptical' | 'rectangular';

const parsePipeShape = (pipeEl: Element): { diameter: number; shape: PipeShape } => {
  const circPipe = pipeEl.querySelector('CircPipe');
  if (circPipe) {
    return { diameter: parseFloat(circPipe.getAttribute('diameter') || '3000') / 1000, shape: 'circular' };
  }

  const eggPipe = pipeEl.querySelector('EggPipe');
  if (eggPipe) {
    const h = parseFloat(eggPipe.getAttribute('height') || '0.3');
    const w = parseFloat(eggPipe.getAttribute('width') || String(h * 0.67));
    return { diameter: Math.max(w, h), shape: 'egg' };
  }

  const elliPipe = pipeEl.querySelector('ElliPipe');
  if (elliPipe) {
    const h = parseFloat(elliPipe.getAttribute('height') || '0.3');
    const w = parseFloat(elliPipe.getAttribute('width') || '0.3');
    return { diameter: Math.max(w, h), shape: 'elliptical' };
  }

  const rectPipe = pipeEl.querySelector('RectPipe');
  if (rectPipe) {
    const h = parseFloat(rectPipe.getAttribute('height') || '0.3');
    const w = parseFloat(rectPipe.getAttribute('width') || '0.3');
    return { diameter: Math.sqrt(w * w + h * h), shape: 'rectangular' };
  }

  return { diameter: 0.3, shape: 'circular' };
};

const buildPipeCylinder = (start: THREE.Vector3, end: THREE.Vector3, radius: number): THREE.BufferGeometry => {
  const direction = new THREE.Vector3().subVectors(end, start);
  const length = direction.length();
  if (length < 0.001) return new THREE.BufferGeometry();

  const geometry = new THREE.CylinderGeometry(radius, radius, length, 8);
  const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const quaternion = new THREE.Quaternion();
  quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.clone().normalize());

  geometry.applyQuaternion(quaternion);
  geometry.translate(midpoint.x, midpoint.y, midpoint.z);
  return geometry;
};

const buildStructGeometry = (struct: ParsedStruct): THREE.BufferGeometry => {
  const radius = (struct.outerDiameter || struct.innerDiameter || 1.0) / 2;
  const height = struct.height || 2.0;
  const [x, y, z] = struct.center;

  const geometry = new THREE.CylinderGeometry(radius, radius, height, 12);
  geometry.rotateX(Math.PI / 2);
  geometry.translate(x, y, z + height / 2);
  return geometry;
};

export const parsePipeNetworks = (doc: Document): PipeNetworkResult => {
  const meshes: LandXMLFeature[] = [];
  const lines: LandXMLFeature[] = [];
  const pipeNetworks = doc.querySelectorAll('PipeNetworks > PipeNetwork');
  // Only resolve/warn when there is actually a <PipeNetworks> container —
  // otherwise every network-less file would pay a redundant "no <Units>"
  // console.warn on top of parseSurfaces's own (mirrors parseAlignments).
  const nonMetreUnit = pipeNetworks.length > 0 ? declaredNonMetreUnit(doc) : null;

  for (const network of Array.from(pipeNetworks)) {
    const networkName = network.getAttribute('name') || 'PipeNetwork';
    const structs = network.querySelectorAll('Structs > Struct');
    const pipes = network.querySelectorAll('Pipes > Pipe');
    // Struct centers, pipe centerlines, diameters and lengths are refused,
    // never scaled (see `declaredNonMetreUnit`). Fires only when this network
    // actually declares a struct or pipe.
    if (nonMetreUnit !== null && (structs.length > 0 || pipes.length > 0)) {
      throw new LandxmlNonMetreUnitsError(nonMetreUnit);
    }

    // Parse structures
    const structMap = new Map<string, ParsedStruct>();
    for (const structEl of Array.from(structs)) {
      const structName = structEl.getAttribute('name') || '';
      const center = structEl.querySelector('Center');
      if (!center?.textContent) continue;

      const [x, y, z] = parsePoint(center.textContent);

      const invertElevs = new Map<string, number>();
      for (const inv of Array.from(structEl.querySelectorAll('InvertElev'))) {
        const elev = parseFloat(inv.getAttribute('elev') || '0');
        const refPipe = inv.getAttribute('refPipe') || '';
        if (refPipe) invertElevs.set(refPipe, elev);
      }

      const circStruct = structEl.querySelector('CircStruct');
      const rectStruct = structEl.querySelector('RectStruct');

      let innerDiameter = 1.0;
      let outerDiameter = 1.2;
      let height = 2.0;

      if (circStruct) {
        innerDiameter = parseFloat(circStruct.getAttribute('diameter') || '1000.0') / 1000;
        outerDiameter = innerDiameter + 0.2;
      } else if (rectStruct) {
        const w = parseFloat(rectStruct.getAttribute('width') || '1.0');
        const l = parseFloat(rectStruct.getAttribute('length') || '1.0');
        innerDiameter = Math.max(w, l);
        outerDiameter = innerDiameter + 0.2;
      }

      const elevSump = parseFloat(structEl.getAttribute('elevSump') || String(z));
      const elevRim = parseFloat(structEl.getAttribute('elevRim') || String(z + 2));
      height = Math.abs(elevRim - elevSump);
      if (height < 0.1) height = 2.0;

      const parsed: ParsedStruct = {
        name: structName,
        center: [x, y, elevSump],
        innerDiameter,
        outerDiameter,
        height,
        invertElevs,
      };
      structMap.set(structName, parsed);

      const structGeo = buildStructGeometry(parsed);
      if (structGeo.getAttribute('position')?.count) {
        meshes.push({
          geometry: structGeo,
          name: `${networkName} - ${structName}`,
          category: 'STRUCTURE',
          metadata: {
            rimElev: elevRim,
            sumpElev: elevSump,
            innerDiameter,
          },
        });
      }
    }

    // Parse pipes
    for (const pipeEl of Array.from(pipes)) {
      const pipeName = pipeEl.getAttribute('name') || 'Pipe';
      const startStruct = structMap.get(pipeEl.getAttribute('refStart') || '');
      const endStruct = structMap.get(pipeEl.getAttribute('refEnd') || '');
      if (!startStruct || !endStruct) continue;

      const { diameter, shape } = parsePipeShape(pipeEl);
      const pipeRadius = diameter / 2;

      const invertStart = startStruct.invertElevs.get(pipeName) ?? startStruct.center[2];
      const invertEnd = endStruct.invertElevs.get(pipeName) ?? endStruct.center[2];
      const startZ = invertStart + pipeRadius;
      const endZ = invertEnd + pipeRadius;

      const startPt = new THREE.Vector3(startStruct.center[0], startStruct.center[1], startZ);
      const endPt = new THREE.Vector3(endStruct.center[0], endStruct.center[1], endZ);
      const length3D = startPt.distanceTo(endPt);
      const horizLength = Math.hypot(
        endStruct.center[0] - startStruct.center[0],
        endStruct.center[1] - startStruct.center[1],
      );
      const slope = horizLength > 0 ? (invertEnd - invertStart) / horizLength : 0;

      const metadata: LandXMLFeatureMetadata = {
        diameter,
        pipeShape: shape,
        invertStart,
        invertEnd,
        slope,
        length: length3D,
      };

      const pipeGeo = buildPipeCylinder(startPt, endPt, pipeRadius);
      if (pipeGeo.getAttribute('position')?.count) {
        meshes.push({
          geometry: pipeGeo,
          name: `${networkName} - ${pipeName}`,
          category: 'PIPE',
          metadata,
        });
      }

      const lineGeo = new THREE.BufferGeometry().setFromPoints([startPt, endPt]);
      lines.push({
        geometry: lineGeo,
        name: `${networkName} - ${pipeName} (centerline)`,
        category: 'PIPE',
        metadata,
      });
    }
  }

  return { meshes, lines };
};
